import { MovieRecommendation, QuestionnaireData } from './geminiRecommendationsService';
import { ProcessedMovie } from './screenshotProcessingService';
import { formatFirebaseDate } from '../utils/dateUtils';

export interface InstantWatchlistData {
  id: string;
  title: string;
  preferences?: QuestionnaireData; // Optional for screenshot-based lists
  recommendations?: MovieRecommendation[]; // For recommendation-based lists
  detectedMovies?: ProcessedMovie[]; // For screenshot-based lists
  source: 'recommendation' | 'screenshot' | 'text' | 'link'; // Source of the watchlist
  generatedAt: string; // ISO timestamp
  posterThumbnails: string[]; // Array of poster URLs
}

class InstantWatchlistService {
  private static STORAGE_KEY = 'instant_watchlist_data';
  private static HISTORY_KEY = 'instant_watchlist_history';
  private static MAX_HISTORY = 3;

  // Save instant watchlist data from recommendations
  static saveInstantWatchlist(
    preferences: QuestionnaireData,
    recommendations: MovieRecommendation[],
    posterThumbnails: string[] = []
  ): InstantWatchlistData {
    const data: InstantWatchlistData = {
      id: `instant-${Date.now()}`,
      title: 'Your Instant Watchlist',
      preferences,
      recommendations,
      source: 'recommendation',
      generatedAt: new Date().toISOString(),
      posterThumbnails,
    };

    try {
      // Save as the current/latest watchlist
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));

      // Also add to history
      this.addToHistory(data);

      console.log('✅ Instant watchlist saved:', data.id);
    } catch (error) {
      console.error('❌ Error saving instant watchlist:', error);
    }

    return data;
  }

  // Add a watchlist to history (keeps last 3)
  private static addToHistory(data: InstantWatchlistData): void {
    try {
      const history = this.getWatchlistHistory();

      // Add new watchlist to the beginning
      history.unshift(data);

      // Keep only the last MAX_HISTORY items
      const trimmedHistory = history.slice(0, this.MAX_HISTORY);

      localStorage.setItem(this.HISTORY_KEY, JSON.stringify(trimmedHistory));
      console.log(`✅ History updated: ${trimmedHistory.length} watchlists`);
    } catch (error) {
      console.error('❌ Error updating history:', error);
    }
  }

  // Get watchlist history (last 3)
  static getWatchlistHistory(): InstantWatchlistData[] {
    try {
      const stored = localStorage.getItem(this.HISTORY_KEY);
      if (stored) {
        return JSON.parse(stored) as InstantWatchlistData[];
      }
    } catch (error) {
      console.error('❌ Error retrieving watchlist history:', error);
    }
    return [];
  }

  // Set a specific watchlist from history as the current one
  static setCurrentWatchlist(watchlist: InstantWatchlistData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(watchlist));
      console.log('✅ Set current watchlist:', watchlist.id);
    } catch (error) {
      console.error('❌ Error setting current watchlist:', error);
    }
  }

  // Save instant watchlist from screenshot detection
  static saveScreenshotWatchlist(detectedMovies: ProcessedMovie[]): InstantWatchlistData {
    const data: InstantWatchlistData = {
      id: `screenshot-${Date.now()}`,
      title: 'From Screenshot',
      detectedMovies,
      source: 'screenshot',
      generatedAt: new Date().toISOString(),
      posterThumbnails: detectedMovies
        .slice(0, 3)
        .map(movie => movie.posterPath ? `https://image.tmdb.org/t/p/w500${movie.posterPath}` : '')
        .filter(url => url),
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      this.addToHistory(data);
      console.log('✅ Screenshot watchlist saved:', data.id);
    } catch (error) {
      console.error('❌ Error saving screenshot watchlist:', error);
    }

    return data;
  }

  // Save instant watchlist from text input
  static saveTextWatchlist(detectedMovies: ProcessedMovie[]): InstantWatchlistData {
    const data: InstantWatchlistData = {
      id: `text-${Date.now()}`,
      title: 'From Text',
      detectedMovies,
      source: 'text',
      generatedAt: new Date().toISOString(),
      posterThumbnails: detectedMovies
        .slice(0, 3)
        .map(movie => movie.posterPath ? `https://image.tmdb.org/t/p/w500${movie.posterPath}` : '')
        .filter(url => url),
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      this.addToHistory(data);
      console.log('✅ Text watchlist saved:', data.id);
    } catch (error) {
      console.error('❌ Error saving text watchlist:', error);
    }

    return data;
  }

  // Save instant watchlist from online link
  static saveLinkWatchlist(detectedMovies: ProcessedMovie[], sourceInfo: string): InstantWatchlistData {
    const data: InstantWatchlistData = {
      id: `link-${Date.now()}`,
      title: `From ${sourceInfo}`,
      detectedMovies,
      source: 'link',
      generatedAt: new Date().toISOString(),
      posterThumbnails: detectedMovies
        .slice(0, 3)
        .map(movie => movie.posterPath ? `https://image.tmdb.org/t/p/w500${movie.posterPath}` : '')
        .filter(url => url),
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      this.addToHistory(data);
      console.log('✅ Link watchlist saved:', data.id);
    } catch (error) {
      console.error('❌ Error saving link watchlist:', error);
    }

    return data;
  }

  // Get the most recent instant watchlist
  static getLatestInstantWatchlist(): InstantWatchlistData | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored) as InstantWatchlistData;
        return data;
      }
    } catch (error) {
      console.error('❌ Error retrieving instant watchlist:', error);
    }
    return null;
  }

  // Update poster thumbnails for the instant watchlist and its history entry
  static updatePosterThumbnails(id: string, posterUrls: string[]): void {
    const urls = posterUrls.slice(0, 4);
    try {
      const current = this.getLatestInstantWatchlist();
      if (current && current.id === id) {
        current.posterThumbnails = urls;
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
      }

      const history = this.getWatchlistHistory();
      const idx = history.findIndex(w => w.id === id);
      if (idx !== -1) {
        history[idx].posterThumbnails = urls;
        localStorage.setItem(this.HISTORY_KEY, JSON.stringify(history));
      }
      console.log('✅ Instant watchlist posters updated');
    } catch (error) {
      console.error('❌ Error updating instant watchlist posters:', error);
    }
  }

  // Clear the instant watchlist
  static clearInstantWatchlist(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('✅ Instant watchlist cleared');
    } catch (error) {
      console.error('❌ Error clearing instant watchlist:', error);
    }
  }

  // Format preferences for display
  static formatPreferencesForDisplay(preferences: QuestionnaireData): string {
    const parts: string[] = [];

    // Add selected moods (excluding 'Any')
    if (preferences.mood.length > 0 && !preferences.mood.includes('Any')) {
      parts.push(preferences.mood.join(', '));
    }

    // Add selected languages (excluding 'Any')
    if (preferences.language.length > 0 && !preferences.language.includes('Any')) {
      parts.push(`in ${preferences.language.join(', ')}`);
    }

    // Add time period (excluding 'Any')
    if (preferences.timePeriod.length > 0 && !preferences.timePeriod.includes('Any')) {
      parts.push(`from ${preferences.timePeriod.join(', ')}`);
    }

    // Add duration (excluding 'Any')
    if (preferences.duration.length > 0 && !preferences.duration.includes('Any')) {
      const durationText = preferences.duration.join(', ').toLowerCase();
      parts.push(`${durationText} runtime`);
    }

    // Add exclusions
    const exclusions: string[] = [];
    if (preferences.excludeWatched) {
      exclusions.push('watched movies');
    }

    if (exclusions.length > 0) {
      parts.push(`excluding ${exclusions.join(' and ')}`);
    }

    return parts.length > 0 ? parts.join(', ') : 'All preferences';
  }

  // Get relative time string
  static getRelativeTime(timestamp: string): string {
    const now = new Date();
    const generated = new Date(timestamp);
    const diffMs = now.getTime() - generated.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return formatFirebaseDate(generated);
    }
  }
}

export default InstantWatchlistService; 